import React, { useEffect, useState, useCallback, useMemo } from "react";
import { CiHeart } from "react-icons/ci";
import { FaLongArrowAltLeft } from "react-icons/fa";
import { Link, useLocation } from "react-router-dom";
import SubjectOverview from "../components/model/SubjectOverview";
import { IoHeart } from "react-icons/io5";
import { useTranslation } from "react-i18next";
import { ApiGet } from "../helper/axiosFunctions";
import { commonToast } from "../helper/Toast";
import SubjectDetailCard from "../components/DeckInfo/SubjectDetailCard";
import { DeckCardSkeleton } from "../components/DeckCard";
import QuestionCard from "../components/QuestionCard";
import { readLocalCardOrder, sortCardsByDeckOrder } from "../helper/deckCardOrder";

export default function SubjectDetail() {
  const { t } = useTranslation();
  const [deckData, setDeckData] = useState(null);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cardLoading, setCardLoading] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [isDetail, setIsDetail] = useState(false);
  const [tab, setTab] = useState("questions");
  const [isLiked, setIsLiked] = useState(false);
  const { pathname } = useLocation();
  const route = pathname.split("/")[2];

  const tabs = [
    { id: "questions", label: t("questions") },
    { id: "about", label: t("about") },
  ];

  const complexityMap = useMemo(() => {
    if (!deckData?.cards) return {};
    const map = {};
    deckData.cards.forEach((card) => {
      map[card.cardId] = card.complexity;
    });
    return map;
  }, [deckData]);

  const orderedCards = useMemo(
    () => sortCardsByDeckOrder(cards, deckData?.cards, readLocalCardOrder(route)),
    [cards, deckData, route]
  );

  const meta = useMemo(() => {
    const grades = (deckData?.schoolGrades || []).filter(
      (g) => g && String(g).trim()
    );
    return [
      deckData?.mainSubject,
      grades.length ? `${t("gradeLabel")}: ${grades.join(", ")}` : null,
    ]
      .filter(Boolean)
      .join(" · ");
  }, [deckData, t]);

  const getCards = useCallback(async () => {
    if (!route) return;

    setCardLoading(true);
    try {
      const response = await ApiGet(`/contents/decks/${route}/cards`);
      setCards(response?.data || []);
    } catch (error) {
      commonToast(error?.data?.error || error?.message, "error");
    } finally {
      setCardLoading(false);
    }
  }, [route]);

  const getDeckInfo = useCallback(async () => {
    if (!route) return;

    setLoading(true);
    try {
      const response = await ApiGet(`/contents/decks/${route}`);
      setDeckData(response?.data);
      if (response?.data?.cards?.length) {
        getCards();
      }
    } catch (error) {
      commonToast(error?.data?.error || error?.message, "error");
    } finally {
      setLoading(false);
    }
  }, [route, getCards]);

  useEffect(() => {
    window.scrollTo(0, 0);
    getDeckInfo();
  }, [getDeckInfo]);

  return (
    <div className="bg-[#F5F5F5] min-h-full">
      <div className="border-b border-gray-200 bg-white">
        <div className="mx-auto max-w-[1400px] px-5 py-5 md:px-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex min-w-0 items-start gap-3">
              <Link
                to="/classes"
                className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full border border-gray-200 text-gray-700 hover:bg-gray-50"
              >
                <FaLongArrowAltLeft />
              </Link>
              <div className="min-w-0 space-y-1.5">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="truncate text-xl font-bold text-gray-900">
                    {loading ? "…" : deckData?.deckName || "…"}
                  </h1>
                </div>
                {meta ? (
                  <p className="truncate text-sm text-lightgray">{meta}</p>
                ) : null}
              </div>
            </div>

            <button
              type="button"
              className="flex size-9 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
              onClick={() => setIsLiked((prev) => !prev)}
              aria-label="Like"
            >
              {isLiked ? (
                <IoHeart className="text-xl text-red-500" />
              ) : (
                <CiHeart className="text-xl" />
              )}
            </button>
          </div>

          <div className="mt-5 flex w-fit max-w-full items-center gap-1.5 overflow-auto rounded-full border border-gray-200 bg-gray-50 p-1.5">
            {tabs.map((item) => (
              <button
                key={item.id}
                type="button"
                disabled={!deckData && item.id !== "questions"}
                onClick={() => setTab(item.id)}
                className={`shrink-0 rounded-full px-4 py-2 text-[15px] transition-colors disabled:opacity-50 ${
                  item.id === tab
                    ? "bg-darkblue font-semibold text-white shadow-sm"
                    : "text-gray-600 hover:bg-white"
                }`}
              >
                {item.id === "questions"
                  ? `${item.label} (${orderedCards.length})`
                  : item.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-5 py-4 md:px-4">
        {tab === "questions" && (
          <>
            {cardLoading && orderedCards.length === 0 && (
              <div className="grid grid-cols-3 gap-3 lg:grid-cols-2 md:!grid-cols-1">
                {Array.from({ length: 3 }).map((_, i) => (
                  <DeckCardSkeleton key={i} />
                ))}
              </div>
            )}

            <div className="grid grid-cols-3 gap-3 lg:grid-cols-2 md:!grid-cols-1">
              {orderedCards?.length > 0 ? (
                orderedCards.map((item, index) => (
                  <QuestionCard
                    key={item.cardId || index}
                    item={item}
                    index={index}
                    complexity={complexityMap[item?.cardId]}
                    onDetail={(card) => {
                      setSelectedCard(card);
                      setIsDetail(true);
                    }}
                  />
                ))
              ) : (
                !cardLoading && (
                  <div className="col-span-3 py-10 text-center text-gray-500">
                    No questions found.
                  </div>
                )
              )}
            </div>
          </>
        )}

        {tab === "about" && <SubjectDetailCard data={deckData} />}
      </div>

      {isDetail && (
        <SubjectOverview
          isDetail={isDetail}
          setIsDetail={setIsDetail}
          data={selectedCard}
          currentDeckId={deckData?.deckId || route}
          complexity={complexityMap[selectedCard?.cardId]}
        />
      )}
    </div>
  );
}
